/**
 * Calendar-date helpers for week-grid features that work over REAL dates (the
 * staff overrides tab, the dashboard schedule calendar). All dates are local
 * YYYY-MM-DD strings — the same grammar as `WeeklySchedule.exceptions` keys —
 * so weekday math is timezone-independent (see shared/schedule.ts).
 */

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** Today as YYYY-MM-DD in the browser's timezone. */
export function todayIso(): string {
  const n = new Date();
  return `${n.getFullYear()}-${pad(n.getMonth() + 1)}-${pad(n.getDate())}`;
}

/** Today as YYYY-MM-DD in a given IANA timezone (the business's "today"). */
export function todayIsoIn(timeZone: string): string {
  return zonedDateAndMinute(new Date(), timeZone).date;
}

export function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y!, m! - 1, d!));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
}

/** The Monday on or before `iso` (weeks display Monday-first). */
export function mondayOf(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const wd = new Date(Date.UTC(y!, m! - 1, d!)).getUTCDay(); // 0=Sun … 6=Sat
  return addDays(iso, wd === 0 ? -6 : 1 - wd);
}

/**
 * These take the UI locale explicitly (doc 20 D5). They used to pass
 * `undefined`, which means "the browser's locale" — so the one language setting
 * the owner actually chose was overridden by one they never saw.
 *
 * `timeZone: 'UTC'` with noon local is unrelated to the business timezone: the
 * input is already a calendar date, and noon-UTC keeps it from sliding a day
 * under any offset.
 */

/** Short column label, e.g. "Mon 14". */
export function labelFor(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', timeZone: 'UTC' }).format(noonUtc(iso));
}

/** The phone pager's single day with its year — "Tue, 6 Oct 2026" (short
 *  month: it shares one line with the arrows and the Today button). */
export function weekdayDateYearLabel(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(noonUtc(iso));
}

/**
 * The pager's week with its year, collapsed the locale's way — "6 – 12 October
 * 2026", "28 September – 4 October 2026", "29 Dec 2026 – 4 Jan 2027" — rather
 * than two full dates glued with a dash.
 */
export function dateRangeYearLabel(fromIso: string, toIso: string, locale: string): string {
  const fmt = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
  // `formatRange` is missing from older Safari/Firefox the build still targets;
  // two full dates are longer but never a crash.
  if (typeof fmt.formatRange !== 'function') return `${fmt.format(noonUtc(fromIso))} – ${fmt.format(noonUtc(toIso))}`;
  return fmt.formatRange(noonUtc(fromIso), noonUtc(toIso));
}

function noonUtc(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d!, 12));
}

/** Weekday names in Monday-first display order, in the reader's language —
 *  replaces the hard-coded `['Mon','Tue',…]` the schedule editors used. */
export function weekdayNames(locale: string, style: 'short' | 'long'): string[] {
  const fmt = new Intl.DateTimeFormat(locale, { weekday: style, timeZone: 'UTC' });
  // 2024-01-01 was a Monday, so +0…+6 walks Monday → Sunday.
  return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(Date.UTC(2024, 0, 1 + i, 12))));
}

const dtfCache = new Map<string, Intl.DateTimeFormat>();

/**
 * Project an instant onto a business-local calendar date + minute-of-day —
 * how an appointment's UTC `start` lands on the week grid.
 */
export function zonedDateAndMinute(instant: Date, timeZone: string): { date: string; min: number } {
  let dtf = dtfCache.get(timeZone);
  if (!dtf) {
    dtf = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
    dtfCache.set(timeZone, dtf);
  }
  const p: Partial<Record<Intl.DateTimeFormatPartTypes, string>> = {};
  for (const part of dtf.formatToParts(instant)) p[part.type] = part.value;
  return {
    date: `${p.year}-${p.month}-${p.day}`,
    min: Number(p.hour) * 60 + Number(p.minute),
  };
}
